import "./FacetFilterComponent.scss";

import * as React from "react";

import PropTypes from "prop-types";
import * as _ from "lodash";
import { FormattedMessage } from "react-intl";
import { FormGroup } from "react-bootstrap";
import { trackFilterFacets } from "../../../../utils/SearchTrackingUtil";
import TextInput from "shared/src/components/TextInput";
import { FacetCheckbox } from "./FacetCheckbox";
import { FacetResult } from "shared/src/types/articlesearch/Facets";

const NUMBER_OF_FACETS_SHOWN = 8;

const trackFacetFiltering = _.debounce((query) => trackFilterFacets(query), 500);

type Props = {
    facet: FacetResult;
    onFacetSelect: (key: string, name: string, newState: boolean) => void;
    onDeselectAllValuesOfAttribute?: (x0: React.SyntheticEvent<HTMLElement>, x1: string) => void;
    selectedFacets: {
        [x: string]: boolean;
    };
    isSecondFacetComponentInFacetWrapper?: boolean | null;
    className: string; // currently only for testing, not actually applied
};

export class FacetFilterComponent extends React.Component<
    Props,
    {
        searchTerm: string;
        showAllFacets: boolean;
    }
> {
    static contextTypes = {
        intl: PropTypes.object
    };

    constructor(props: Props) {
        super(props);
        this.state = {
            searchTerm: "",
            showAllFacets: false
        };
    }

    getSelectedFacetArray(): Array<{
        name: string;
        displayName?: string;
        amount: number;
    }> {
        return _.filter(this.props.facet.values, (value) => this.props.selectedFacets[value.name]);
    }

    onSearchInputChange(event: { [x: string]: any }) {
        event.preventDefault();
        const query = event.target.value;
        this.setState({
            searchTerm: query
        });
        trackFacetFiltering(query);
    }

    showAllFacets(event: { [x: string]: any }) {
        event.preventDefault();
        this.setState({
            showAllFacets: !this.state.showAllFacets
        });
    }

    createFacetSearchField(facet: FacetResult) {
        let msg = "";
        if (!_.isEmpty(facet.name) && this.context.intl) {
            msg = this.context.intl.formatMessage(
                { id: "EX_FILTER_PANEL_FACET_SEARCH_PLACEHOLDER" },
                { facetName: facet.name }
            );
        } else if (this.context.intl) {
            msg = this.context.intl.formatMessage({
                id: "EX_FILTER_PANEL_FACET_SEARCH_PLACEHOLDER_" + facet.key.toUpperCase()
            });
        }
        const inputValue = this.state.searchTerm || "";

        return (
            <form className="test-facet-search-input-form" onSubmit={(event) => event.preventDefault()}>
                <FormGroup>
                    <TextInput
                        type="text"
                        value={inputValue}
                        placeholder={msg}
                        onChange={(event: { [x: string]: any }) => this.onSearchInputChange(event)}
                    />
                </FormGroup>
            </form>
        );
    }

    createFacet(key: string, name: string, displayName: string, amount: number, selected: boolean) {
        return (
            <FacetCheckbox
                key={name}
                name={displayName}
                amount={amount}
                selected={selected}
                onChange={(newState) => this.handleFacetSelect(key, name, newState)}
            />
        );
    }

    handleFacetSelect(key: string, name: string, newState: boolean) {
        this.setState({
            searchTerm: "",
            showAllFacets: false
        });
        this.props.onFacetSelect(key, name, newState);
    }

    createFacetDeselect(key: string) {
        return (
            <div>
                <a
                    className="ex-deselect-facet-link test-deselect-facet"
                    href="#"
                    onClick={(event) => this.handleFacetDeselect(event, key)}>
                    <FormattedMessage id="EX_FILTER_PANEL_FACET_DESELECT" />
                </a>
            </div>
        );
    }

    handleFacetDeselect(event: React.SyntheticEvent<HTMLElement>, key: string) {
        this.setState({
            searchTerm: "",
            showAllFacets: false
        });
        if (this.props.onDeselectAllValuesOfAttribute) {
            this.props.onDeselectAllValuesOfAttribute(event, key);
        }
    }

    createShowAllFacetsButton(hiddenFacetCount: number) {
        return (
            <div className="ex-show-all-facets">
                <a
                    className="ex-show-all-facets-link test-show-all-facets"
                    href="#"
                    onClick={(event: { [x: string]: any }) => this.showAllFacets(event)}>
                    <FormattedMessage
                        id="EX_FILTER_PANEL_FACET_SHOW_ALL"
                        values={{ numberOfFacets: hiddenFacetCount }}
                    />
                </a>
            </div>
        );
    }

    render() {
        const selectedValues = this.getSelectedFacetArray();
        const filteredFacets = _.filter(
            this.props.facet.values,
            (value) =>
                _.includes(value.name.toLowerCase(), this.state.searchTerm.toLowerCase()) &&
                !this.props.selectedFacets[value.name]
        );

        const unselectedValuesShown = this.state.showAllFacets
            ? filteredFacets
            : filteredFacets.slice(0, NUMBER_OF_FACETS_SHOWN - selectedValues.length);
        const hiddenFacetCount = filteredFacets.length + selectedValues.length - NUMBER_OF_FACETS_SHOWN;

        const facetSearchField =
            this.props.facet.values && this.props.facet.values.length > NUMBER_OF_FACETS_SHOWN
                ? this.createFacetSearchField(this.props.facet)
                : null;
        const selectedFacets = _.map(selectedValues, (value) =>
            this.createFacet(this.props.facet.key, value.name, value.displayName ?? value.name, value.amount, true)
        );
        const facetDeselectAllValue =
            selectedValues.length > 1 && this.props.onDeselectAllValuesOfAttribute
                ? this.createFacetDeselect(this.props.facet.key)
                : null;
        const hrOrNull = selectedValues.length > 0 && unselectedValuesShown.length > 0 ? <hr /> : null;
        const unselectedFacets = _.map(unselectedValuesShown, (value) =>
            this.createFacet(this.props.facet.key, value.name, value.displayName ?? value.name, value.amount, false)
        );
        const showAllFacetsButton =
            hiddenFacetCount > 0 && !this.state.showAllFacets ? this.createShowAllFacetsButton(hiddenFacetCount) : null;

        const className = this.props.isSecondFacetComponentInFacetWrapper
            ? "is-second-facet-in-wrapper test-is-second-facet"
            : "";
        return (
            <div className="facet-filter-component test-facet-filter-component">
                <div className={className}>
                    {facetSearchField}
                    {selectedFacets}
                    {facetDeselectAllValue}
                    {hrOrNull}
                    {unselectedFacets}
                    {showAllFacetsButton}
                </div>
            </div>
        );
    }
}
